import React, { useEffect, useState } from 'react';
import { claimsAPI, analyticsAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { FileText, TrendingUp, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { CLAIM_STATUS, formatDate, formatCurrency, getStatusColor, getPriorityColor } from '@/utils/constants';
import { Link } from 'react-router-dom';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const BrokerDashboard = () => {
  const [claims, setClaims] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [claimsRes, analyticsRes] = await Promise.all([
        claimsAPI.getAllForBroker(),
        analyticsAPI.getDashboard(),
      ]);
      setClaims(claimsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClaims = claims.filter(claim => {
    if (filter === 'all') return true;
    if (filter === 'pending') return [CLAIM_STATUS.SUBMITTED, CLAIM_STATUS.UNDER_REVIEW].includes(claim.status);
    if (filter === 'urgent') return claim.priority === 'urgent' || claim.priority === 'high';
    return claim.status === filter;
  });

  const statusChartData = analytics ? Object.entries(analytics.status_counts).map(([status, count]) => ({
    name: status,
    value: count,
  })) : [];

  const typeChartData = analytics ? Object.entries(analytics.type_counts).map(([type, count]) => ({
    name: type,
    value: count,
  })) : [];

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Broker Dashboard
        </h1>
        <p className="text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
          Overview of all claims and performance metrics
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Total Claims
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              <span className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {analytics?.total_claims || 0}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Under Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              <span className="text-3xl font-bold text-yellow-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {analytics?.status_counts[CLAIM_STATUS.UNDER_REVIEW] || 0}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Investigation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-purple-600" />
              <span className="text-3xl font-bold text-purple-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {analytics?.status_counts[CLAIM_STATUS.INVESTIGATION] || 0}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Approved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-3xl font-bold text-green-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {analytics?.status_counts[CLAIM_STATUS.APPROVED] || 0}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Settled
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              <span className="text-3xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {analytics?.status_counts[CLAIM_STATUS.SETTLED] || 0}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Claims by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => entry.name}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Claims by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={typeChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#2563EB" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Claims List */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>All Claims</CardTitle>
            <div className="flex gap-2">
              <button
                data-testid="filter-all-button"
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${filter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button
                data-testid="filter-pending-button"
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${filter === 'pending' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                onClick={() => setFilter('pending')}
              >
                Pending
              </button>
              <button
                data-testid="filter-urgent-button"
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${filter === 'urgent' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                onClick={() => setFilter('urgent')}
              >
                Urgent
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Claim ID</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Type</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Insured</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Status</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Priority</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Est. Loss</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredClaims.map((claim) => (
                  <tr key={claim.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="py-3 px-4">
                      <Link to={`/claims/${claim.id}`}>
                        <span className="font-mono text-sm font-medium text-blue-600 hover:text-blue-800" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {claim.claim_id}
                        </span>
                      </Link>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700" style={{ fontFamily: 'Public Sans, sans-serif' }}>{claim.claim_type}</td>
                    <td className="py-3 px-4 text-sm text-slate-700" style={{ fontFamily: 'Public Sans, sans-serif' }}>{claim.insured_name}</td>
                    <td className="py-3 px-4">
                      <Badge className={getStatusColor(claim.status)}>{claim.status}</Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={getPriorityColor(claim.priority)}>{claim.priority}</Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700" style={{ fontFamily: 'Public Sans, sans-serif' }}>{formatCurrency(claim.estimated_loss)}</td>
                    <td className="py-3 px-4 text-sm text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>{formatDate(claim.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredClaims.length === 0 && (
              <div className="text-center py-8 text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                No claims found
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrokerDashboard;
